import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { City, cities } from '@/data/cities';
import { toast } from '@/components/ui/use-toast';
import { ChevronRight, MapPin, Phone, Mail, ArrowRight, CheckCircle2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageSEO from '@/components/seo/PageSEO';
import BreadcrumbSchema from '@/components/seo/BreadcrumbSchema';
import { Progress } from '@/components/ui/progress';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/components/ui/accordion';

// --- Helpers ---

function getAppointmentRange(slug: string): { min: number; max: number } {
  const big = new Set(["callcenter-antwerpen", "callcenter-gent", "callcenter-brussel"]);
  const mid = new Set([
    "callcenter-leuven", "callcenter-hasselt", "callcenter-mechelen",
    "callcenter-kortrijk", "callcenter-brugge", "callcenter-schaarbeek",
    "callcenter-elsene", "callcenter-etterbeek", "callcenter-evere",
    "callcenter-jette", "callcenter-ganshoren", "callcenter-sint-niklaas",
    "callcenter-aalst", "callcenter-genk", "callcenter-oostende", "callcenter-roeselare",
  ]);
  if (big.has(slug)) return { min: 6, max: 10 };
  if (mid.has(slug)) return { min: 5, max: 8 };
  return { min: 3, max: 6 };
}

// --- Differentiators (shared across all cities) ---

const differentiators = [
  {
    number: "01",
    title: "Eigen data: twee miljoen Belgische bedrijven",
    description: (stad: string) =>
      `Wij targeten bedrijven in ${stad} op sector, omzet en bedrijfsgrootte via onze eigen database. Geen gekochte lijsten, maar een continu bijgewerkte bron die wij zelf beheren.`
  },
  {
    number: "02",
    title: "Vast team, geen rotatie",
    description: () =>
      "Uw agent werkt exclusief voor u. Niet eenmalig en dan iemand anders, maar dezelfde persoon die uw markt en boodschap door en door kent."
  },
  {
    number: "03",
    title: "Realtime dashboard",
    description: () =>
      "U ziet live wie gebeld wordt, wat besproken is en welke afspraken ingepland zijn. Elke afspraak bevat bedrijfsinfo en gespreksnotities. Geen maandrapport achteraf."
  },
  {
    number: "04",
    title: "Capaciteitsmodel naar keuze",
    description: () =>
      "Start met een kwart FTE voor 37,5 uur per maand. Schaal op naar een half of volledig FTE naarmate de samenwerking groeit. Geen langetermijncontract vereist voor de start."
  },
  {
    number: "05",
    title: "Volledig ontzorgd",
    description: () =>
      "Data, script, tooling en opvolging zijn inbegrepen. U focust op closen, wij zorgen voor de volle agenda."
  },
  {
    number: "06",
    title: "Elke cyclus beter",
    description: () =>
      "Scripts en targeting worden bijgesteld op basis van wat werkt. Geen vaste aanpak, maar een systeem dat verbetert naarmate het loopt."
  }
];

// --- Werkwijze steps (shared) ---

const werkwijzeSteps = (stad: string) => [
  {
    step: 1,
    title: "Gratis verkennend gesprek",
    description: `Wij leren uw bedrijf en doelgroep in ${stad} kennen. Samen bepalen wij het ideale klantprofiel en de sectoren waarop wij ons richten.`
  },
  {
    step: 2,
    title: "Data en script",
    description: `Wij selecteren de juiste bedrijven uit onze database van twee miljoen Belgische records. Het script wordt afgestemd op uw sector en de markt in ${stad}.`
  },
  {
    step: 3,
    title: "Dagelijkse prospectie",
    description: "Uw vast toegewezen agent belt dagelijks. Elke prospect wordt systematisch opgevolgd tot er contact en een concrete afspraak is. U volgt alles live in het dashboard."
  },
  {
    step: 4,
    title: "Afspraken in uw agenda",
    description: "Elke afspraak wordt direct ingepland met bedrijfsinfo en gespreksnotities. U weet precies met wie u spreekt en wat er al besproken is."
  }
];

// --- Generic FAQ fallbacks ---

const getGenericFaqItems = (stad: string) => [
  {
    question: `Hoe bereikt u de beslissers in ${stad}?`,
    answer: `Wij gebruiken onze eigen database van twee miljoen Belgische bedrijfsrecords. Voor ${stad} selecteren wij specifiek bedrijven op basis van sector, bedrijfsgrootte en omzet. Wij bellen niet willekeurig maar gericht.`
  },
  {
    question: "Hoe snel kunnen jullie starten?",
    answer: "Na een eerste gesprek starten wij doorgaans binnen twee weken. De eerste concrete afspraken zijn zichtbaar na vier tot zes weken. Data, script en tooling zijn inbegrepen."
  },
  {
    question: `Wat kost telefonische prospectie in ${stad}?`,
    answer: "Wij werken met een capaciteitsmodel. U start met een kwart FTE voor 37,5 uur per maand en schaalt op naarmate de samenwerking groeit. Vraag ons team naar de exacte invulling voor uw situatie."
  },
  {
    question: "Hoeveel afspraken mag ik verwachten?",
    answer: "Gemiddeld vijf tot acht kwalitatieve afspraken per week per verkoper. De eerste vier tot zes weken zijn een opbouwfase. Daarna draait de campagne op kruissnelheid."
  },
  {
    question: "Hoe meet ik het resultaat?",
    answer: "Via ons realtime dashboard ziet u live wie gebeld wordt en welke afspraken ingepland zijn. Elke afspraak bevat bedrijfsinfo en gespreksnotities. Permanent zicht, geen maandrapport achteraf."
  }
];

// --- 3-Step Hero Form ---

const HeroForm = ({ stad }: { stad: string }) => {
  const [step, setStep] = useState(1);
  const [sellers, setSellers] = useState('');
  const [challenge, setChallenge] = useState('');
  const [company, setCompany] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sellerOptions = ['1 tot 2', '3 tot 5', '6 tot 15', 'Meer dan 15'];
  const challengeOptions = ['Te weinig afspraken', 'Geen tijd om te prospecteren', 'Pipeline te klein', 'Slechte opvolging van leads'];

  const handleSubmit = async () => {
    if (!company.trim() || !name.trim() || !phone.trim()) return;
    if (website) return; // honeypot
    
    setIsSubmitting(true);
    try {
      // Submit form data
      const formData = {
        bedrijfsnaam: company,
        naam: name,
        telefoon: phone,
        email: email || undefined,
        verkopers: sellers,
        uitdaging: challenge,
        bron: `Stadspagina ${stad}`,
        website: undefined
      };
      
      console.log('City page form submission:', formData);
      
      // Simulate submission delay
      await new Promise(resolve => setTimeout(resolve, 800));
      setSubmitted(true);
    } catch {
      toast({
        variant: "destructive",
        title: "Er ging iets mis",
        description: "Probeer het later opnieuw of bel ons op 0800 90 500."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 text-center border border-white/20">
        <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="h-8 w-8 text-white" />
        </div>
        <h3 className="text-xl font-bold text-white mb-2">Bedankt, {name}!</h3>
        <p className="text-white/80">We bellen u vandaag nog terug.</p>
      </div>
    );
  }

  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
      <div className="flex items-center gap-2 mb-4">
        <span className="bg-green-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
          Antwoord binnen 2 uur
        </span>
      </div>

      {/* Title + subtext */}
      <h3 className="text-white text-xl font-bold mb-2">Plan uw strategiegesprek</h3>
      <p className="text-white/70 text-sm mb-5">
        In 20 minuten berekenen we samen hoeveel gekwalificeerde afspraken u per maand kunt verwachten.
      </p>

      {/* Progress bar */}
      <div className="mb-6">
        <div className="flex justify-between text-xs text-white/60 mb-2">
          <span>Stap {step} van 3</span>
        </div>
        <Progress value={(step / 3) * 100} className="h-2 bg-white/20" ariaLabel="Formulier voortgang" />
      </div>

      {step === 1 && (
        <div>
          <h3 className="text-white font-semibold mb-4">Hoeveel verkopers heeft uw bedrijf?</h3>
          <div className="grid grid-cols-2 gap-3">
            {sellerOptions.map(option => (
              <button
                key={option}
                onClick={() => { setSellers(option); setStep(2); }}
                className="bg-white/10 hover:bg-white/20 border border-white/20 hover:border-white/40 text-white py-3 px-4 rounded-lg text-sm font-medium transition-all text-left"
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          <h3 className="text-white font-semibold mb-4">Wat is uw grootste uitdaging?</h3>
          <div className="grid grid-cols-1 gap-3">
            {challengeOptions.map(option => (
              <button
                key={option}
                onClick={() => { setChallenge(option); setStep(3); }}
                className="bg-white/10 hover:bg-white/20 border border-white/20 hover:border-white/40 text-white py-3 px-4 rounded-lg text-sm font-medium transition-all text-left"
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 3 && (
        <div>
          <h3 className="text-white font-semibold mb-4">Uw gegevens</h3>
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Bedrijfsnaam"
              value={company}
              onChange={e => setCompany(e.target.value)}
              className="w-full bg-white/10 border border-white/20 text-white placeholder:text-white/50 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-white/40"
            />
            <input
              type="text"
              placeholder="Naam"
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full bg-white/10 border border-white/20 text-white placeholder:text-white/50 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-white/40"
            />
            <input
              type="tel"
              placeholder="Telefoonnummer"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              className="w-full bg-white/10 border border-white/20 text-white placeholder:text-white/50 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-white/40"
            />
            <input
              type="email"
              placeholder="E-mailadres (optioneel)"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full bg-white/10 border border-white/20 text-white placeholder:text-white/50 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-white/40"
            />
            {/* Honeypot */}
            <input
              type="text"
              value={website}
              onChange={e => setWebsite(e.target.value)}
              className="absolute -left-[9999px] opacity-0"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !company.trim() || !name.trim() || !phone.trim()}
              className="w-full bg-[#2D6FAD] hover:bg-[#2D6FAD]/90 text-white py-3 rounded-lg font-semibold text-base"
            >
              {isSubmitting ? 'Verzenden...' : 'Plan mijn gesprek'}
            </Button>
            <p className="text-center text-white/70 text-xs">
              Gemiddeld teruggebeld binnen 2 uur op werkdagen.
            </p>
            <p className="text-center text-white/50 text-xs">100% gratis en vrijblijvend</p>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Main Component ---

const CityPage = () => {
  const { citySlug } = useParams<{ citySlug: string }>();
  const city = cities.find((c) => c.slug === citySlug);
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!city) {
      toast({
        variant: "destructive",
        title: "Stad niet gevonden",
        description: "We konden geen informatie vinden over deze stad.",
      });
    }
  }, [city]);

  if (!city) {
    return (
      <div className="container mt-24 mb-16">
        <h1 className="text-3xl font-bold mb-6">Pagina niet gevonden</h1>
        <p>We kunnen geen informatie vinden over deze locatie.</p>
        <Link to="/diensten" className="text-brand-blue hover:underline mt-4 inline-block">
          Terug naar diensten
        </Link>
      </div>
    );
  }

  const { min, max } = getAppointmentRange(citySlug || '');

  // Resolve data with fallbacks
  const heroSubtitle = city.heroSubtitle || city.intro;
  const localMarketText = city.localMarketText || city.localContext || '';
  const regionAnchoring = city.regionAnchoring || city.uniqueSellingPoint || '';

  // Internal nav links: use prompt data or fallback to provincial neighbors
  const navCityLinks = city.internalNavLinks && city.internalNavLinks.length > 0
    ? city.internalNavLinks
    : city.provincie
      ? cities
        .filter(c => c.provincie === city.provincie && c.slug !== city.slug)
        .slice(0, 5)
        .map(c => c.slug)
      : [];

  const navCities = navCityLinks
    .map(slug => {
      const fullSlug = slug.startsWith('callcenter-') ? slug : `callcenter-${slug}`;
      return cities.find(c => c.slug === fullSlug);
    })
    .filter(Boolean) as City[];

  // Nearby links (for chips section)
  const nearbyLinks = city.nabijgelegen_links
    .map(slug => cities.find(c => c.slug === slug))
    .filter(Boolean) as City[];

  // FAQ items: use new faqItems or fallback to localFaqItems + generics
  const faqItems = city.faqItems && city.faqItems.length >= 5
    ? city.faqItems
    : city.localFaqItems && city.localFaqItems.length > 0
      ? [...city.localFaqItems, ...getGenericFaqItems(city.stad).slice(0, 5 - city.localFaqItems.length)]
      : getGenericFaqItems(city.stad);

  // Provincie display name for nav
  const provincieDisplay = city.provincie || 'Vlaanderen';

  // --- Structured Data ---
  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": `Afsprakenmaker ${city.stad}`,
      "@id": "https://afsprakenmaker.be/#organization",
      "url": `https://afsprakenmaker.be/diensten/${city.slug}`,
      "telephone": "0800 90 500",
      "email": "info@afsprakenmaker.be",
      "logo": "https://afsprakenmaker.be/logo-2026.png",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Aalst",
        "addressRegion": "Oost-Vlaanderen",
        "postalCode": "9300",
        "streetAddress": "Moorselbaan 391"
      },
      "areaServed": {
        "@type": "City",
        "name": city.stad,
        ...(city.wikidataId && { "sameAs": `https://www.wikidata.org/wiki/${city.wikidataId}` }),
        ...(city.postcode && { "postalCode": city.postcode }),
        ...(city.provincie && { "addressRegion": city.provincie })
      },
      "description": city.metaDescription || `B2B callcenter en telemarketing diensten in ${city.stad}. Afsprakenmaker helpt bedrijven met telefonische prospectie en leadgeneratie.`
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqItems.slice(0, 5).map(item => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.answer
        }
      }))
    }
  ];

  const breadcrumbItems = [
    { name: "Home", url: "/" },
    { name: "Diensten", url: "/diensten" },
    ...(city.provincie ? [{ name: city.provincie, url: "/diensten" }] : []),
    { name: city.stad, url: `/diensten/${city.slug}` }
  ];

  const metaTitle = city.metaTitle || `B2B Callcenter ${city.stad} | Afsprakenmaker`;
  const metaDescription = city.metaDescription || `Professionele B2B telemarketing en leadgeneratie in ${city.stad}. ${min}–${max} afspraken per week per verkoper. Vraag een gratis gesprek aan.`;

  return (
    <>
      <PageSEO
        title={metaTitle}
        description={metaDescription}
        keywords={`B2B callcenter ${city.stad}, telemarketing ${city.stad}, afspraken maken ${city.stad}, leadgeneratie ${city.stad}, verkoop uitbesteden`}
        canonicalUrl={`/diensten/${city.slug}`}
        structuredData={structuredData}
      />
      <BreadcrumbSchema items={breadcrumbItems} />

      {/* 1. DARK HERO */}
      <section className="bg-[#1A2540] pt-28 pb-16">
        <div className="container">
          {/* Breadcrumb */}
          <div className="flex items-center text-sm text-white/50 mb-6 flex-wrap">
            <Link to="/" className="hover:text-white/80 transition-colors">Home</Link>
            <ChevronRight className="h-4 w-4 mx-2" />
            <Link to="/diensten" className="hover:text-white/80 transition-colors">Diensten</Link>
            {city.provincie && (
              <>
                <ChevronRight className="h-4 w-4 mx-2" />
                <span>{city.provincie}</span>
              </>
            )}
            <ChevronRight className="h-4 w-4 mx-2" />
            <span className="text-white font-medium">{city.stad}</span>
          </div>

          <div className="grid lg:grid-cols-5 gap-10 items-start">
            {/* Left: Content */}
            <div className="lg:col-span-3">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                B2B Prospectie en Afspraken in {city.stad}
              </h1>
              <p className="text-lg text-white/70 mb-8 leading-relaxed max-w-2xl">
                {heroSubtitle}
              </p>

              {/* Stats row */}
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <div className="text-3xl font-bold text-white">{min} tot {max}</div>
                  <div className="text-sm text-white/50 mt-1">Afspraken per week</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-white">2M+</div>
                  <div className="text-sm text-white/50 mt-1">Belgische bedrijfsrecords</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-white">15 jaar</div>
                  <div className="text-sm text-white/50 mt-1">B2B prospectie</div>
                </div>
              </div>
            </div>

            {/* Right: Form */}
            <div className="lg:col-span-2">
              <HeroForm stad={city.stad} />
            </div>
          </div>
        </div>
      </section>

      {/* 2. INTERNAL NAVIGATION BAR */}
      <section className="bg-[#F5F7FB] border-b border-border">
        <div className="container">
          <div className="flex items-center gap-4 py-3 overflow-x-auto text-sm">
            <span className="font-semibold text-foreground whitespace-nowrap">
              OOK ACTIEF IN {provincieDisplay.toUpperCase()}
            </span>
            <div className="flex items-center gap-3 flex-wrap">
              {navCities.map(c => (
                <Link
                  key={c.slug}
                  to={`/diensten/${c.slug}`}
                  className="text-brand-blue hover:text-brand-darkBlue transition-colors whitespace-nowrap"
                >
                  {c.stad}
                </Link>
              ))}
              <span className="text-border">|</span>
              <Link to="/diensten" className="text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap">
                Alle diensten
              </Link>
              <Link to="/over-ons" className="text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap">
                Onze aanpak
              </Link>
              <Link to="/over-ons" className="text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap">
                Over ons
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 3. LOCAL MARKET SECTION */}
      {localMarketText && (
        <section className="bg-[#F5F7FB] py-16">
          <div className="container">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8">
              Het bedrijvenlandschap in {city.stad}
            </h2>
            <div className="grid md:grid-cols-5 gap-8">
              <div className="md:col-span-3">
                <p className="text-[#2C3E50] leading-relaxed text-lg">
                  {localMarketText}
                </p>
              </div>
              {regionAnchoring && (
                <div className="md:col-span-2">
                  <div className="bg-white rounded-xl p-6 border border-border shadow-sm">
                    <h3 className="font-semibold text-foreground mb-3">Onze verankering in de regio</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {regionAnchoring}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* 4. DIFFERENTIATORS */}
      <section className="bg-white py-16">
        <div className="container">
          <p className="text-sm font-semibold text-brand-blue uppercase tracking-wider mb-2">WAAROM AFSPRAKENMAKER</p>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-10">
            Wat ons onderscheidt in {city.stad}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {differentiators.map(diff => (
              <div key={diff.number} className="border border-border rounded-xl p-6 hover:shadow-md transition-shadow">
                <div className="text-brand-blue font-bold text-sm mb-2">{diff.number}</div>
                <h3 className="font-semibold text-foreground mb-3">{diff.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {diff.description(city.stad)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. RESULTS */}
      <section className="bg-[#1A2540] py-16">
        <div className="container">
          <p className="text-sm font-semibold text-white/50 uppercase tracking-wider mb-2">WAT U MAG VERWACHTEN</p>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-10">
            Meetbare resultaten in {city.stad}
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white/10 rounded-xl p-6 border border-white/10">
              <div className="text-4xl font-bold text-white mb-1">{min} tot {max}</div>
              <div className="text-white/60 text-sm">Kwalitatieve afspraken per week per verkoper</div>
            </div>
            <div className="bg-white/10 rounded-xl p-6 border border-white/10">
              <div className="text-4xl font-bold text-white mb-1">4 tot 6 weken</div>
              <div className="text-white/60 text-sm">Tijd tot de eerste meetbare resultaten</div>
            </div>
            <div className="bg-white/10 rounded-xl p-6 border border-white/10">
              <div className="text-4xl font-bold text-white mb-1">100.000+</div>
              <div className="text-white/60 text-sm">Afspraken geboekt voor Belgische bedrijven sinds 2012</div>
            </div>
          </div>
          <p className="text-xs text-white/30 mt-6">
            * Resultaten hangen af van sector, doelgroep en propositie. Bovenstaande cijfers zijn gebaseerd op gemiddelden van vergelijkbare campagnes.
          </p>
        </div>
      </section>

      {/* 6. COST COMPARISON */}
      <section className="bg-[#F5F7FB] py-16">
        <div className="container">
          <p className="text-sm font-semibold text-brand-blue uppercase tracking-wider mb-2">DE BELGISCHE REALITEIT</p>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-10">
            Intern werven kost meer dan het lijkt
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl p-8 border border-border">
              <h3 className="font-bold text-foreground text-xl mb-4">Intern</h3>
              <p className="text-muted-foreground leading-relaxed">
                Een SDR in loondienst kost gemiddeld 48 euro per uur all-in. Maar een interne SDR besteedt doorgaans de helft van zijn tijd aan andere zaken: opleiding, admin en CRM. Effectieve kost: ongeveer 96 euro per prospectie-uur.
              </p>
            </div>
            <div className="bg-white rounded-xl p-8 border-2 border-brand-blue">
              <h3 className="font-bold text-brand-blue text-xl mb-4">Met Afsprakenmaker</h3>
              <p className="text-muted-foreground leading-relaxed">
                Onze agent werkt 100% op prospectie. Uw verkoper focust op closen in plaats van bellen.
              </p>
              <p className="text-xs text-muted-foreground mt-4">
                Bron: Eurostat 2024 en Salesforce State of Sales, 6e editie.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 7. WERKWIJZE */}
      <section className="bg-white py-16">
        <div className="container">
          <p className="text-sm font-semibold text-brand-blue uppercase tracking-wider mb-2">ONZE AANPAK</p>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-10">
            Zo werken wij in {city.stad}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {werkwijzeSteps(city.stad).map(step => (
              <div key={step.step} className="relative">
                <div className="text-5xl font-bold text-brand-blue/10 mb-2">{step.step}</div>
                <h3 className="font-semibold text-foreground mb-3">{step.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. TESTIMONIAL */}
      <section className="bg-[#1A2540] py-16">
        <div className="container max-w-3xl text-center">
          <blockquote className="text-xl md:text-2xl text-white font-medium italic leading-relaxed mb-6">
            &ldquo;Ons marktaandeel groeide snel en ons vermogen om deals te sluiten verbeterde drastisch.&rdquo;
          </blockquote>
          <div className="text-white/60">
            — Werner Temmerman, Sales Director Belgium bij Penneo
          </div>
        </div>
      </section>

      {/* 9. FAQ */}
      <section className="bg-[#F5F7FB] py-16">
        <div className="container max-w-3xl">
          <p className="text-sm font-semibold text-brand-blue uppercase tracking-wider mb-2">VEELGESTELDE VRAGEN</p>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8">
            Vragen over prospectie in {city.stad}
          </h2>
          <Accordion type="single" collapsible className="bg-white rounded-xl border border-border overflow-hidden">
            {faqItems.slice(0, 5).map((item, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-b border-border last:border-0">
                <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-muted/50 transition-colors">
                  <span className="text-left font-medium text-foreground">{item.question}</span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-5 pt-1 text-muted-foreground leading-relaxed">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* 10. NEARBY CITIES */}
      {nearbyLinks.length > 0 && (
        <section className="bg-white py-16">
          <div className="container">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-brand-blue uppercase tracking-wider">OOK ACTIEF IN DE BUURT</span>
            </div>
            <p className="text-muted-foreground mb-6">
              Wij prospecteren in heel {provincieDisplay}
            </p>
            <div className="flex flex-wrap gap-3">
              {nearbyLinks.map(c => (
                <Link
                  key={c.slug}
                  to={`/diensten/${c.slug}`}
                  className="bg-muted hover:bg-muted/80 px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5"
                >
                  <MapPin className="h-3.5 w-3.5 text-brand-blue" />
                  {c.stad}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 11. BOTTOM CTA */}
      <section className="bg-[#2D6FAD] py-16">
        <div className="container text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Klaar om te groeien in {city.stad}?
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            Laat uw gegevens achter voor een vrijblijvend gesprek. Wij bellen u vandaag nog terug.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center bg-white text-[#2D6FAD] font-semibold py-3 px-8 rounded-lg hover:bg-white/90 transition-colors shadow-lg"
          >
            Gratis gesprek aanvragen
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>

          <div className="grid md:grid-cols-3 gap-8 mt-12 max-w-2xl mx-auto text-white/80 text-sm">
            <div className="flex items-center justify-center gap-2">
              <Phone className="h-4 w-4" />
              <span>0800 90 500</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <Mail className="h-4 w-4" />
              <span>info@afsprakenmaker.be</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              <span>Persoonlijke aanpak</span>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default CityPage;
